import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/modules/shared/ui/components/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shared/ui/components/select"
import { DatePicker } from "@/modules/shared/ui/components/date-picker"
import { Plus, X } from "lucide-react"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { workflowService } from "../../infrastructure/workflow-service"

export interface DocumentToBeSigned {
  file: File
  name: string
}

const employeeFormSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.email("Invalid email address"),
  jobTitle: z.string().min(1, "Job title is required"),
  jobStartDate: z.date(),
  documentsNeeded: z.array(z.string()).optional(),
  workflowTemplateIds: z.array(z.string()).optional(),
  documentsToBeSigned: z.array(z.custom<DocumentToBeSigned>()).optional(),
})

export type EmployeeFormValues = z.infer<typeof employeeFormSchema>

interface EmployeeFormProps {
  onSubmit: (values: EmployeeFormValues) => void
  onCancel?: () => void
  defaultValues?: Partial<EmployeeFormValues>
  submitLabel?: string
  formId?: string
  showActions?: boolean
  availableJobs?: Array<{ id: string; title: string }>
  readOnlyFields?: string[]
}

export default function EmployeeForm({
  onSubmit,
  onCancel,
  defaultValues,
  submitLabel = "Create Employee",
  formId = "employee-form",
  showActions = true,
  availableJobs,
  readOnlyFields = []
}: EmployeeFormProps) {
  const navigate = useNavigate()
  const [currentDocument, setCurrentDocument] = useState("")
  const { currentOrganization } = useOrganizationsService()
  const [workflows, setWorkflows] = useState<
    Array<{ id: string; name: string; isActive: boolean }>
  >([])
  const [loadingWorkflows, setLoadingWorkflows] = useState(false)


  const form = useForm<EmployeeFormValues>({
    resolver: zodResolver(employeeFormSchema),
    defaultValues: {
      firstName: defaultValues?.firstName ?? "",
      lastName: defaultValues?.lastName ?? "",
      email: defaultValues?.email ?? "",
      jobTitle: defaultValues?.jobTitle ?? "",
      jobStartDate: defaultValues?.jobStartDate,
      documentsNeeded: defaultValues?.documentsNeeded ?? [],
      workflowTemplateIds: defaultValues?.workflowTemplateIds ?? [],
      documentsToBeSigned: defaultValues?.documentsToBeSigned ?? [],
    },
  })

  const isReadOnly = (fieldName: string) => readOnlyFields.includes(fieldName)

  const handleAddDocument = () => {
    const doc = currentDocument.trim()
    const current = form.getValues("documentsNeeded") || []
    if (doc && !current.includes(doc)) {
      form.setValue("documentsNeeded", [...current, doc])
      setCurrentDocument("")
    }
  }

  const handleRemoveDocument = (index: number) => {
    const current = form.getValues("documentsNeeded") || []
    form.setValue(
      "documentsNeeded",
      current.filter((_, i) => i !== index),
    )
  }

  useEffect(() => {
    if (currentOrganization?.id) {
      setLoadingWorkflows(true)
      workflowService
        .getWorkflows(currentOrganization.id, false)
        .then((data) => {
          setWorkflows(data)
        })
        .catch(() => { })
        .finally(() => {
          setLoadingWorkflows(false)
        })
    }
  }, [currentOrganization?.id])

  return (
    <Form {...form}>
      <form
        id={formId}
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  First Name <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="John"
                    readOnly={isReadOnly("firstName")}
                    className={
                      isReadOnly("firstName")
                        ? "bg-muted cursor-not-allowed opacity-70"
                        : ""
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Last Name <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Doe"
                    readOnly={isReadOnly("lastName")}
                    className={
                      isReadOnly("lastName")
                        ? "bg-muted cursor-not-allowed opacity-70"
                        : ""
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Email <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  placeholder="john.doe@company.com"
                  readOnly={isReadOnly("email") && (field.value == null || field.value == undefined)}
                  className={
                    (isReadOnly("email") && (field.value == null || field.value == undefined))
                      ? "bg-muted cursor-not-allowed opacity-70"
                      : ""
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-4">
          <FormField
            control={form.control}
            name="jobTitle"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Job Title <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Frontend Developer"
                    readOnly={isReadOnly("jobTitle")}
                    className={
                      isReadOnly("jobTitle")
                        ? "bg-muted cursor-not-allowed opacity-70"
                        : ""
                    }
                    list={
                      availableJobs &&
                        availableJobs.length > 0 &&
                        !isReadOnly("jobTitle")
                        ? "job-title-suggestions"
                        : undefined
                    }
                  />
                </FormControl>
                {availableJobs &&
                  availableJobs.length > 0 &&
                  !isReadOnly("jobTitle") && (
                    <datalist id="job-title-suggestions">
                      {availableJobs.map((job) => (
                        <option key={job.id} value={job.title} />
                      ))}
                    </datalist>
                  )}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="jobStartDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Job Start Date <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <DatePicker
                    value={field.value ? new Date(field.value) : undefined}
                    onChange={field.onChange}
                    placeholder="Pick start date"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Auto-Start Workflows */}
          <FormField
            control={form.control}
            name="workflowTemplateIds"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Auto-Start Workflows (Optional)</FormLabel>
                {workflows && workflows.length > 0 ? (
                  <>
                    <Select
                      value=""
                      onValueChange={(workflowId) => {
                        if (workflowId && !field.value?.includes(workflowId)) {
                          field.onChange([...(field.value || []), workflowId])
                        }
                      }}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue
                            placeholder={
                              loadingWorkflows
                                ? "Loading..."
                                : "Select workflows to auto-start"
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-card">
                        {workflows.map((workflow) => (
                          <SelectItem
                            className="hover:bg-muted cursor-pointer"
                            key={workflow.id}
                            value={workflow.id}
                          >
                            {workflow.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {field.value && field.value.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {field.value.map((id) => {
                          const workflow = workflows.find((w) => w.id === id)
                          return workflow ? (
                            <div
                              key={id}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm"
                            >
                              <span>{workflow.name}</span>
                              <Button
                                type="button"
                                onClick={() =>
                                  field.onChange(
                                    field.value?.filter((wId) => wId !== id) ||
                                    [],
                                  )
                                }
                                className="hover:text-blue-900"
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          ) : null
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="rounded-md border border-dashed border-border p-4 bg-muted">
                    <p className="text-sm text-muted-foreground">
                      You don't have any workflow templates yet. Create one to
                      automate onboarding steps.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3"
                      onClick={() => navigate("/onboarding/workflows")}
                    >
                      Go to Workflow Templates
                    </Button>
                  </div>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Documents Needed */}
        <FormField
          control={form.control}
          name="documentsNeeded"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Documents Needed from Employee</FormLabel>
              <div className="flex gap-2">
                <Input
                  value={currentDocument}
                  onChange={(e) => setCurrentDocument(e.target.value)}
                  placeholder="e.g., ID, Driver's License, Certificate"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleAddDocument()
                    }
                  }}
                />
                <Button
                  type="button"
                  onClick={handleAddDocument}
                  disabled={!currentDocument.trim()}
                  className="shrink-0"
                >
                  <Plus className="h-4 w-4" />
                  Add
                </Button>
              </div>
              {field.value && field.value.length > 0 && (
                <div className="mt-3 space-y-2">
                  {field.value.map((doc, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-muted p-2 rounded-md border border-border"
                    >
                      <span className="text-sm text-foreground">{doc}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveDocument(index)}
                        className="h-6 w-6 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        {showActions && (
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit">{submitLabel}</Button>
          </div>
        )}
      </form>
    </Form>
  )
}
